import { useState, useEffect, useRef } from 'react';
import { SiLangchain, SiOpenai, SiGradio } from 'react-icons/si';
import { BsDatabase } from 'react-icons/bs';
import { FaMicrophone } from 'react-icons/fa';

// ── THEME ────────────────────────────────────────────────
const css = `
  :root {
    --bg: #0d0d0d;
    --bg2: #141414;
    --bg3: #1a1a1a;
    --border: rgba(255,255,255,0.07);
    --border-md: rgba(255,255,255,0.12);
    --border-hover: rgba(255,255,255,0.22);
    --text: #f0ece5;
    --text-2: #8a8278;
    --text-3: #4a4640;
    --accent: #c9b99a;
    --green: #4ade80;
    --serif: 'Cormorant Garamond', Georgia, serif;
    --sans: 'DM Sans', -apple-system, sans-serif;
    --mono: 'DM Mono', 'Courier New', monospace;
    --ease: cubic-bezier(0.16, 1, 0.3, 1);
    --max: 1140px;
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(22px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes blink {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0; }
  }
  @keyframes termglow {
    0%, 100% { box-shadow: 0 0 20px 2px rgba(74,222,128,0.04), 0 32px 64px rgba(0,0,0,0.5); }
    50%       { box-shadow: 0 0 40px 8px rgba(74,222,128,0.10), 0 32px 64px rgba(0,0,0,0.5); }
  }
  @keyframes gridPulse {
    0%, 100% { opacity: 0.5; }
    50%       { opacity: 0.8; }
  }
  @keyframes pageFadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .reveal { opacity: 0; transform: translateY(18px); transition: opacity 0.7s var(--ease), transform 0.7s var(--ease); }
  .reveal.visible { opacity: 1; transform: translateY(0); }
  .fade-1 { animation: fadeUp 0.8s var(--ease) 0.1s both; }
  .fade-2 { animation: fadeUp 0.8s var(--ease) 0.25s both; }
  .fade-3 { animation: fadeUp 0.8s var(--ease) 0.4s both; }
  .fade-4 { animation: fadeUp 0.8s var(--ease) 0.55s both; }
  .cursor-blink {
    display: inline-block; width: 8px; height: 14px;
    background: var(--green); margin-left: 2px;
    animation: blink 1.1s step-end infinite; vertical-align: middle;
  }
  .page-enter { animation: pageFadeUp 0.5s var(--ease) both; }
  html { scroll-behavior: smooth; }
  @keyframes scrollDrop {
    0%   { transform: scaleY(0); transform-origin: top; opacity: 0; }
    40%  { transform: scaleY(1); transform-origin: top; opacity: 1; }
    80%  { transform: scaleY(1); transform-origin: bottom; opacity: 1; }
    100% { transform: scaleY(0); transform-origin: bottom; opacity: 0; }
  }
`;

// ── DATA ────────────────────────────────────────────────
const PROJECTS = [
  {
    num: '01',
    title: 'CV Screening Automation',
    desc: 'Automated resume screening pipeline using semantic embeddings and NLP to score and rank candidates against job descriptions. Eliminates manual CV review at scale.',
    tags: ['Python', 'NLP', 'Sentence Transformers', 'PyMuPDF', 'pandas'],
    github: 'https://github.com/rpanchal126',
    images: [],
    detail: {
      problem: 'Recruiters waste hours manually screening resumes that don\'t match the role.',
      solution: 'Automated pipeline that extracts text from PDFs, generates semantic embeddings, and scores each candidate against the job description by meaning — not just keywords.',
      impact: 'Eliminates manual CV filtering at the initial screening stage. Surfaces the most relevant candidates regardless of how they phrase their experience.',
      stack: ['Python', 'NLP', 'Sentence Transformers', 'PyMuPDF', 'pandas'],
    },
  },
  {
    num: '02',
    title: 'Rec.ai',
    desc: 'End-to-end pipeline that transcribes meeting audio with Whisper, extracts structured notes via GPT-4, and auto-pushes summaries directly to Notion.',
    tags: ['Python', 'OpenAI Whisper', 'GPT-4', 'Notion API', 'pydub'],
    github: 'https://github.com/rpanchal126',
    images: [
      { src: '/images/notion-output.png', alt: 'Notion output showing structured meeting notes', label: 'Notion Output' },
    ],
    detail: {
      problem: 'Professionals lose 15–20 minutes after every meeting writing notes and chasing action items. Context gets lost.',
      solution: 'End-to-end pipeline — audio in → Whisper transcription → GPT-4 structured extraction of summary, action items, decisions, owners → auto-pushed to Notion via API.',
      impact: 'Meeting notes generated and synced to Notion in under 2 minutes. Action items are structured, assigned, and never lost.',
      stack: ['Python', 'OpenAI Whisper', 'GPT-4', 'Notion API', 'pydub'],
    },
  },
  {
    num: '03',
    title: 'PaperMind',
    desc: 'Production RAG app for research papers with hybrid BM25 + dense vector retrieval, LangChain orchestration, Streamlit UI, and LangSmith observability.',
    tags: ['Python', 'LangChain', 'ChromaDB', 'BM25', 'OpenAI', 'Streamlit', 'LangSmith'],
    github: 'https://github.com/rpanchal126/PaperMind',
    images: [
      { src: '/images/papermind-ui.png', alt: 'PaperMind Streamlit UI showing RAG Q&A interface', label: 'Streamlit UI' },
    ],
    detail: {
      problem: 'Researchers waste hours skimming dense papers to find one specific answer. Standard LLMs hallucinate on documents they haven\'t seen.',
      solution: 'Production RAG app with hybrid BM25 + ChromaDB dense retrieval fused via Reciprocal Rank Fusion. LangChain orchestrates the full pipeline. LangSmith traces every run.',
      impact: 'Accurate, grounded answers from any uploaded research paper in seconds — with source chunks shown. Hybrid retrieval outperforms single-method search on domain-specific content.',
      stack: ['Python', 'LangChain', 'ChromaDB', 'BM25', 'OpenAI', 'Streamlit', 'LangSmith'],
    },
  },
  {
    num: '04',
    title: 'PaperMind Monitor',
    desc: 'Standalone RAG evaluation dashboard with RAGAS metrics, LLM-as-judge scoring achieving 4.8/5, with score history and trend tracking over time.',
    tags: ['Python', 'RAGAS', 'GPT-4o', 'LangChain', 'Gradio', 'SQLite'],
    github: 'https://github.com/rpanchal126/PaperMind-Monitor-PPM',
    images: [
      { src: '/images/ppm-run.png',     alt: 'Evaluation run results',    label: 'Evaluation Run',  main: true },
      { src: '/images/ppm-history.png', alt: 'Score history table',       label: 'Score History' },
      { src: '/images/ppm-trends.png',  alt: 'Metric trend charts',       label: 'Trend Tracking' },
      { src: '/images/ppm-judge.png',   alt: 'LLM-as-judge per-question', label: 'LLM Judge' },
    ],
    detail: {
      problem: 'Most RAG apps are shipped without structured evaluation. Developers eyeball outputs with no way to detect regressions or prove quality.',
      solution: 'Standalone RAG evaluation dashboard — upload PDFs and ground truth CSV, get RAGAS metrics plus LLM-as-judge scoring with per-question reasoning. All runs persist in SQLite with trend tracking.',
      impact: 'Final eval run: RAGAS overall 0.648 · LLM Judge average 4.80/5 · 9 of 10 answers rated Excellent.',
      stack: ['Python', 'RAGAS', 'GPT-4o', 'LangChain', 'Gradio', 'SQLite'],
    },
  },
  {
    num: '05',
    title: 'Fetch — Local AI Desktop Assistant',
    desc: 'A local AI assistant that runs entirely on your machine. No cloud, no subscriptions, no data leaving your device. Fetch can see your files, read documents, and answer questions — powered by Llama 3.2 via Ollama. Ships as a native Mac app.',
    tags: ['Python', 'FastAPI', 'LangChain', 'ChromaDB', 'React', 'Electron', 'Ollama'],
    github: 'https://github.com/rpanchal126/fetch',
    images: [
      { src: '/Fetchss.png', alt: 'Fetch local AI desktop assistant UI', label: 'App UI', main: true },
    ],
    detail: {
      problem: 'AI assistants require cloud access, subscriptions, and send your data to external servers. There is no privacy-first alternative that works like a real desktop app.',
      solution: 'Fetch runs a local LLM (Llama 3.2 3B) via Ollama on your machine. A LangChain agent with real filesystem tools lets it list files, read documents, and search your computer. FastAPI serves as a headless backend. React + Electron delivers a native desktop experience with five themes and a rainbow draw-on splash animation.',
      impact: 'Fully functional local AI assistant shipped as a 365MB Mac DMG. Real filesystem access — no hallucination on file queries. Private, offline, and fast. Demonstrates the full stack from local LLM inference to native desktop distribution.',
      stack: ['Python', 'FastAPI', 'LangChain', 'ChromaDB', 'Ollama', 'Llama 3.2', 'React', 'Vite', 'Electron', 'PyInstaller', 'LangSmith'],
    },
  },
];

const STACK = [
  { cat: 'Languages',   items: ['Python', 'SQL', 'Bash'] },
  { cat: 'ML / AI',     items: ['LangChain', 'OpenAI · Whisper', 'Sentence Transformers'] },
  { cat: 'Retrieval',   items: ['ChromaDB', 'BM25', 'RAGAS'] },
  { cat: 'Data',        items: ['pandas', 'PyMuPDF', 'SQLite'] },
  { cat: 'APIs & Infra',items: ['Notion API', 'LangSmith', 'pydub'] },
  { cat: 'UI / Deploy', items: ['Streamlit', 'Gradio', 'GitHub Pages'] },
];

// ── HOOKS ────────────────────────────────────────────────
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('visible'); io.unobserve(el); } },
      { threshold: 0.08 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

// ── COMPONENTS ──────────────────────────────────────────

function Nav({ goHome, isProjectPage }) {
  const handleNav = (id) => {
    if (isProjectPage) {
      goHome(id);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  };
  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 60px',
      background: 'rgba(13,13,13,0.92)', backdropFilter: 'blur(14px)',
      borderBottom: '0.5px solid var(--border)',
    }}>
      <button
        onClick={() => goHome(null)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 300,
          color: 'var(--text-2)', letterSpacing: '0.08em',
        }}
      >
        ronpanchal.work
      </button>
      <ul style={{ display: 'flex', gap: 36, listStyle: 'none' }}>
        {['projects', 'about', 'contact'].map(id => (
          <li key={id}>
            <button onClick={() => handleNav(id)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 300,
              color: 'var(--text-2)', letterSpacing: '0.03em',
              padding: 0,
            }}>
              {id.charAt(0).toUpperCase() + id.slice(1)}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function TerminalWithRef({ codeRef }) {
  const lines = [
    { type: 'prompt', cmd: 'python engineer.py' },
    { type: 'blank' },
    { type: 'comment', text: '# Loading profile...' },
    { type: 'blank' },
    { type: 'kv', key: 'name    ', val: '"Ronak Panchal"', color: '#86efac' },
    { type: 'kv', key: 'role    ', val: '"AI/ML Engineer → AI PM"', color: '#86efac' },
    { type: 'kv', key: 'location', val: '"USA"', color: '#86efac' },
    { type: 'blank' },
    { type: 'kv', key: 'stack   ', val: '[', color: '#f0ece5', noQuote: true },
    { type: 'indent', text: '"LangChain", "OpenAI", "RAG",' },
    { type: 'indent', text: '"ChromaDB", "Whisper", "RAGAS"' },
    { type: 'raw', text: ']', color: '#4a4640' },
    { type: 'blank' },
    { type: 'kv', key: 'projects', val: '4', color: '#f9a8d4', comment: '# shipped' },
    { type: 'kv', key: 'rag_score', val: '4.8/5.0', color: '#f9a8d4', comment: '# LLM-as-judge' },
    { type: 'blank' },
    { type: 'cursor', cmd: 'status' },
    { type: 'output', text: '✓ Open to AI PM roles' },
  ];

  return (
    <div style={{
      background: '#0a0a0a',
      border: '0.5px solid rgba(255,255,255,0.1)',
      borderRadius: 12, overflow: 'hidden',
      animation: 'termglow 3s ease-in-out infinite',
      fontFamily: 'var(--mono)',
    }}>
      <div style={{
        background: '#141414', borderBottom: '0.5px solid rgba(255,255,255,0.07)',
        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8,
      }}>
        {[['#ff5f57'], ['#febc2e'], ['#28c840']].map(([c], i) => (
          <span key={i} style={{ width: 11, height: 11, borderRadius: '50%', background: c, display: 'inline-block' }} />
        ))}
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', marginLeft: 'auto', marginRight: 'auto' }}>
          ronak@portfolio ~ python
        </span>
      </div>
      <div ref={codeRef} style={{ padding: '28px 32px 32px', fontSize: 15, lineHeight: 1.9 }}>
        {lines.map((l, i) => {
          if (l.type === 'blank')   return <div key={i} style={{ height: 8 }} />;
          if (l.type === 'comment') return <div key={i} style={{ color: '#3d3a36' }}>{l.text}</div>;
          if (l.type === 'prompt')  return <div key={i}><span style={{ color: '#4ade80' }}>❯ </span><span style={{ color: 'var(--text)' }}>{l.cmd}</span></div>;
          if (l.type === 'kv')      return <div key={i}><span style={{ color: '#93c5fd' }}>{l.key}</span><span style={{ color: '#4a4640' }}> = </span><span style={{ color: l.color }}>{l.val}</span>{l.comment && <span style={{ color: '#3d3a36' }}>  {l.comment}</span>}</div>;
          if (l.type === 'indent')  return <div key={i} style={{ color: '#86efac', paddingLeft: 16 }}>{l.text}</div>;
          if (l.type === 'raw')     return <div key={i} style={{ color: l.color }}>{l.text}</div>;
          if (l.type === 'cursor')  return <div key={i}><span style={{ color: '#4ade80' }}>❯ </span><span style={{ color: 'var(--text)' }}>{l.cmd}</span><span className="cursor-blink" /></div>;
          if (l.type === 'output')  return <div key={i} style={{ color: '#8a8278', paddingLeft: 16 }}>{l.text}</div>;
          return null;
        })}
      </div>
    </div>
  );
}

function Btn({ children, onClick, primary, small }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: small ? '9px 18px' : '12px 26px',
        borderRadius: 7,
        fontFamily: 'var(--sans)', fontSize: small ? 12 : 13, fontWeight: 400,
        letterSpacing: '0.02em', cursor: 'pointer',
        background: primary ? '#242220' : '#1e1e1e',
        color: hovered ? 'var(--text)' : (primary ? 'var(--text)' : 'var(--text-2)'),
        border: `0.5px solid ${hovered ? 'rgba(255,255,255,0.3)' : (primary ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.1)')}`,
        transform: hovered ? 'scale(1.05)' : 'scale(1)',
        boxShadow: hovered ? '0 0 20px 4px rgba(201,185,154,0.13), 0 0 40px 8px rgba(201,185,154,0.06)' : 'none',
        transition: 'all 0.25s cubic-bezier(0.16,1,0.3,1)',
      }}
    >
      {children}
    </button>
  );
}

function SectionLabel({ children }) {
  const ref = useReveal();
  return (
    <div ref={ref} className="reveal" style={{
      fontFamily: 'var(--mono)', fontSize: 24, fontWeight: 300,
      color: 'var(--text-3)', letterSpacing: '0.18em', textTransform: 'uppercase',
      marginBottom: 52, display: 'flex', alignItems: 'center', gap: 18,
    }}>
      {children}
      <span style={{ flex: 1, height: '0.5px', background: 'var(--border)' }} />
    </div>
  );
}

// ── IMAGE SLOT ── shows image if available, styled placeholder if not
function ImageSlot({ src, alt, label }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const showPlaceholder = !src || error;

  return (
    <div>
      {showPlaceholder ? (
        <div style={{
          width: '100%', aspectRatio: '16/9',
          background: 'var(--bg3)',
          border: '0.5px solid var(--border)',
          borderRadius: 8,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 10,
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <span style={{
            fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)',
            letterSpacing: '0.1em', textTransform: 'uppercase',
          }}>
            {src ? src.replace('/', '') : 'screenshot pending'}
          </span>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          style={{
            width: '100%', display: 'block',
            borderRadius: 8, border: '0.5px solid var(--border)',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.3s',
          }}
        />
      )}
      {label && (
        <p style={{
          fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)',
          letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 10,
        }}>{label}</p>
      )}
    </div>
  );
}

// ── PROJECT CARD ──────────────────────────────────────────
function ProjectCard({ project, onOpen }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid', gridTemplateColumns: '52px 1fr 24px',
        gap: '0 24px', alignItems: 'start',
        padding: '30px 34px',
        background: hovered ? 'var(--bg2)' : 'var(--bg)',
        borderBottom: '0.5px solid var(--border)',
        color: 'inherit',
        position: 'relative', overflow: 'hidden',
        transition: 'background 0.2s',
        cursor: 'pointer',
      }}
    >
      <span style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: 2,
        background: 'var(--accent)',
        transform: hovered ? 'scaleY(1)' : 'scaleY(0)',
        transformOrigin: 'bottom',
        transition: 'transform 0.35s cubic-bezier(0.16,1,0.3,1)',
      }} />
      <span style={{ fontFamily: 'var(--mono)', fontSize: 24, color: 'var(--text-3)', letterSpacing: '0.06em', paddingTop: 2 }}>{project.num}</span>
      <div>
        <p style={{ fontSize: 16, fontWeight: 400, color: 'var(--text)', letterSpacing: '-0.01em', marginBottom: 7 }}>{project.title}</p>
        <p style={{ fontSize: 13, fontWeight: 300, color: 'var(--text-2)', lineHeight: 1.7, marginBottom: 14, maxWidth: 560 }}>{project.desc}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {project.tags.map(t => (
            <span key={t} style={{
              fontFamily: 'var(--mono)', fontSize: 10,
              color: hovered ? 'var(--text-2)' : 'var(--tag-text)',
              background: '#161412',
              border: `0.5px solid ${hovered ? 'rgba(255,255,255,0.12)' : 'var(--border)'}`,
              padding: '3px 8px', borderRadius: 3, letterSpacing: '0.04em',
              transition: 'color 0.2s, border-color 0.2s',
            }}>{t}</span>
          ))}
        </div>
      </div>
      <a
        href={project.github}
        target="_blank"
        rel="noreferrer"
        onClick={e => e.stopPropagation()}
        style={{
          fontSize: 16, color: hovered ? 'var(--accent)' : 'var(--text-3)',
          paddingTop: 2,
          transform: hovered ? 'translate(3px,-3px)' : 'translate(0,0)',
          transition: 'transform 0.25s cubic-bezier(0.16,1,0.3,1), color 0.2s',
          textDecoration: 'none', display: 'block',
        }}
      >↗</a>
    </div>
  );
}

// ── PROJECT PAGE (full dedicated page) ───────────────────
function ProjectPage({ project, onBack }) {
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [project]);

  return (
    <main className="page-enter" style={{ paddingTop: 80, minHeight: '100vh' }}>
      <div style={{ maxWidth: 'var(--max)', margin: '0 auto', padding: '52px 60px 120px' }}>

        {/* Back button */}
        <BackBtn onClick={onBack} />

        {/* Header */}
        <div style={{ marginTop: 44, marginBottom: 52 }}>
          <span style={{
            fontFamily: 'var(--mono)', fontSize: 24, color: 'var(--text-3)', letterSpacing: '0.1em',
          }}>{project.num}</span>
          <h1 style={{
            fontFamily: 'var(--serif)',
            fontSize: 'clamp(38px, 5vw, 66px)',
            fontWeight: 300, lineHeight: 1.0, letterSpacing: '-0.02em',
            color: 'var(--text)', marginTop: 8, marginBottom: 28,
          }}>{project.title}</h1>
          <GithubBtn href={project.github} />
        </div>

        {/* Images */}
        {(() => {
          const imgs = project.images || [];
          const hasMain = imgs[0]?.main;
          if (hasMain) {
            // Main image full-width, remaining in 2×2 grid below
            const [main, ...rest] = imgs;
            return (
              <div style={{ marginBottom: 64 }}>
                <div style={{ marginBottom: 14 }}>
                  <ImageSlot src={main.src} alt={main.alt} label={main.label} />
                </div>
                {rest.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    {rest.map((img, i) => (
                      <ImageSlot key={i} src={img.src} alt={img.alt} label={img.label} />
                    ))}
                  </div>
                )}
              </div>
            );
          }
          if (imgs.length > 1) {
            return (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 64 }}>
                {imgs.map((img, i) => (
                  <ImageSlot key={i} src={img.src} alt={img.alt} label={img.label} />
                ))}
              </div>
            );
          }
          return (
            <div style={{ marginBottom: 64 }}>
              <ImageSlot src={imgs[0]?.src} alt={imgs[0]?.alt} label={imgs[0]?.label} />
            </div>
          );
        })()}

        <Divider />

        {/* Problem / Solution / Impact */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
          gap: 52, margin: '56px 0',
        }}>
          {[
            { label: 'Problem',  text: project.detail.problem },
            { label: 'Solution', text: project.detail.solution },
            { label: 'Impact',   text: project.detail.impact },
          ].map(({ label, text }) => (
            <div key={label}>
              <p style={{
                fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)',
                letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 16,
              }}>{label}</p>
              <p style={{ fontSize: 14, fontWeight: 300, color: 'var(--text-2)', lineHeight: 1.9 }}>{text}</p>
            </div>
          ))}
        </div>

        <Divider />

        {/* Stack */}
        <div style={{ marginTop: 44 }}>
          <p style={{
            fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)',
            letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 16,
          }}>Stack</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {project.detail.stack.map(t => (
              <span key={t} style={{
                fontFamily: 'var(--mono)', fontSize: 11,
                color: 'var(--text-2)', background: '#161412',
                border: '0.5px solid var(--border-md)',
                padding: '5px 12px', borderRadius: 3, letterSpacing: '0.04em',
              }}>{t}</span>
            ))}
          </div>
        </div>

      </div>
    </main>
  );
}

// ── SMALL SHARED ATOMS ───────────────────────────────────

function BackBtn({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        fontFamily: 'var(--mono)', fontSize: 11,
        color: hovered ? 'var(--text-2)' : 'var(--text-3)',
        letterSpacing: '0.08em',
        display: 'inline-flex', alignItems: 'center', gap: 8,
        transition: 'color 0.2s',
      }}
    >
      <span style={{
        display: 'inline-block',
        transform: hovered ? 'translateX(-3px)' : 'translateX(0)',
        transition: 'transform 0.2s',
      }}>←</span>
      Back to Projects
    </button>
  );
}

function GithubBtn({ href }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontFamily: 'var(--mono)', fontSize: 11,
        color: hovered ? 'var(--text)' : 'var(--text-2)',
        textDecoration: 'none',
        border: `0.5px solid ${hovered ? 'var(--border-hover)' : 'var(--border-md)'}`,
        background: hovered ? 'var(--bg2)' : 'transparent',
        padding: '7px 16px', borderRadius: 4, letterSpacing: '0.04em',
        transition: 'all 0.2s',
      }}
    >
      View on GitHub ↗
    </a>
  );
}

function Divider() {
  return <div style={{ height: '0.5px', background: 'var(--border)' }} />;
}

function SkillBar({ label, pct }) {
  const trackRef = useRef(null);
  const barRef   = useRef(null);
  useEffect(() => {
    const track = trackRef.current;
    const bar   = barRef.current;
    if (!track || !bar) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        bar.style.width = `${pct}%`;
        io.unobserve(track);
      }
    }, { threshold: 0.1 });
    io.observe(track);
    return () => io.disconnect();
  }, [pct]);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
        <span style={{ fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 300, color: 'var(--text-2)' }}>{label}</span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.06em' }}>{pct}%</span>
      </div>
      <div ref={trackRef} style={{ height: 2, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
        <div ref={barRef} style={{
          height: '100%', width: 0,
          background: 'linear-gradient(90deg, rgba(240,236,229,0.75) 0%, rgba(240,236,229,0.3) 100%)',
          borderRadius: 2,
          transition: 'width 1.2s cubic-bezier(0.16, 1, 0.3, 1)',
          boxShadow: '0 0 10px rgba(240,236,229,0.2)',
        }} />
      </div>
    </div>
  );
}

function ContactLink({ href, label, sub, external, download }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer' : undefined}
      download={download || undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 18px', borderRadius: 7,
        border: `0.5px solid ${hovered ? 'var(--border-hover)' : 'var(--border)'}`,
        textDecoration: 'none',
        color: hovered ? 'var(--text)' : 'var(--text-2)',
        background: hovered ? 'var(--bg2)' : 'transparent',
        fontSize: 14, fontWeight: 300,
        transform: hovered ? 'translateX(5px)' : 'translateX(0)',
        transition: 'all 0.2s',
      }}
    >
      {label}
      <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)' }}>{sub}</span>
    </a>
  );
}

// ── SECTIONS ────────────────────────────────────────────

function TechIcon({ Icon, label }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'default' }}
    >
      <Icon size={24} style={{
        color: hovered ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)',
        transition: 'color 0.2s',
      }} />
      <span style={{
        fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: hovered ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.2)',
        transition: 'color 0.2s',
      }}>{label}</span>
    </div>
  );
}

function Hero({ onViewProjects }) {
  const terminalWrapRef = useRef(null);
  const terminalCodeRef = useRef(null);
  const backdropRef     = useRef(null);
  const stickyRef       = useRef(null);
  const textRef         = useRef(null);
  const gridRef         = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const y  = window.scrollY;
      const vh = window.innerHeight;
      // animation completes at y = vh*1.5 (1.5 viewport heights of scrolling — slow & cinematic)
      // hero section is 250vh, sticky unpins at 250vh - 100vh = 150vh = vh*1.5
      // so p=1 exactly when sticky unpins — no dead scroll space
      const raw = Math.min(1, y / (vh * 1.5));
      // ease-in-out so the animation starts gently, speeds up, then eases to a stop
      const p = raw < 0.5 ? 2 * raw * raw : 1 - Math.pow(-2 * raw + 2, 2) / 2;

      if (terminalWrapRef.current) {
        const scale = 1 + p * 5.5;
        terminalWrapRef.current.style.transform = `scale(${scale})`;
      }

      // Backdrop fills any gap edges early
      if (backdropRef.current) {
        backdropRef.current.style.opacity = String(Math.min(1, p * 2.5));
      }

      // Code fades out as expansion starts
      if (terminalCodeRef.current) {
        const codeOpacity = Math.max(0, 1 - (p - 0.1) / 0.3);
        terminalCodeRef.current.style.opacity = String(codeOpacity);
      }

      // Left text fades and drifts up
      if (textRef.current) {
        textRef.current.style.opacity   = String(Math.max(0, 1 - p * 1.4));
        textRef.current.style.transform = `translateY(${p * -36}px)`;
      }

      // Grid dims
      if (gridRef.current) {
        gridRef.current.style.opacity = String(Math.max(0, 1 - p * 1.5));
      }

      // Fade the entire sticky container to 0 near the end of the animation.
      // When the sticky element unsticks and scrolls away it will be invisible —
      // no black panel sliding up, About just appears from below.
      if (stickyRef.current) {
        const heroOpacity = p < 0.7 ? 1 : Math.max(0, 1 - (p - 0.7) / 0.3);
        stickyRef.current.style.opacity = String(heroOpacity);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    // 250vh total: sticky height (100vh) + animation scroll distance (150vh = vh*1.5)
    // sticky unpins exactly when p hits 1 — no dead space, About arrives from below
    <section id="hero" style={{ height: '250vh', position: 'relative' }}>
      <div ref={stickyRef} style={{
        position: 'sticky', top: 0,
        height: '100vh', width: '100%',
        display: 'flex', alignItems: 'center',
        padding: '0 60px',
        overflow: 'hidden',
        background: 'var(--bg)',
      }}>
        {/* Grid bg */}
        <div ref={gridRef} style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
          backgroundSize: '72px 72px',
          maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, rgba(0,0,0,0.4) 0%, transparent 100%)',
          animation: 'gridPulse 6s ease-in-out infinite',
        }} />

        {/* Soft accent glow behind the heading */}
        <div style={{
          position: 'absolute', top: '10%', left: '-5%',
          width: '55%', height: '80%', pointerEvents: 'none',
          background: 'radial-gradient(ellipse 60% 60% at 40% 50%, rgba(201,185,154,0.055) 0%, transparent 70%)',
        }} />

        {/* Backdrop — fills the whole viewport behind the terminal as it expands,
            so no edges or colour mismatches are visible */}
        <div ref={backdropRef} style={{
          position: 'absolute', inset: 0,
          background: 'var(--bg)',
          opacity: 0,
          pointerEvents: 'none',
          zIndex: 0,
        }} />

        <div style={{
          maxWidth: 'var(--max)', width: '100%', margin: '0 auto',
          display: 'grid', gridTemplateColumns: '1fr 1.35fr', gap: 64, alignItems: 'center',
          position: 'relative', zIndex: 1,
        }}>
          {/* LEFT — fades & drifts up */}
          <div ref={textRef} className="fade-1" style={{ willChange: 'opacity, transform' }}>
            <p style={{
              fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 300,
              color: 'var(--text-2)', letterSpacing: '0.16em', textTransform: 'uppercase',
              marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ width: 24, height: '0.5px', background: 'var(--accent)', display: 'inline-block' }} />
              AI / ML Engineer
            </p>
            <h1 style={{
              fontFamily: 'var(--serif)',
              fontSize: 'clamp(65px, 7.5vw, 110px)',
              fontWeight: 300, lineHeight: 0.95, letterSpacing: '-0.02em',
              color: 'var(--text)',
            }}>
              Ronak<br />
              <em style={{ fontStyle: 'italic', color: 'var(--accent)' }}>Panchal.</em>
            </h1>
            <p style={{
              fontSize: 15, fontWeight: 300, color: 'var(--text-2)',
              maxWidth: 380, lineHeight: 1.8, marginTop: 24, marginBottom: 20,
            }}>
              AI/ML Engineer building toward an AI Product Manager role.
            </p>

            {/* Ticker */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginBottom: 18,
              fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)',
            }}>
              <span style={{
                width: 5, height: 5, borderRadius: '50%',
                background: 'var(--green)', display: 'inline-block', flexShrink: 0,
                boxShadow: '0 0 6px rgba(74,222,128,0.6)',
                animation: 'blink 2s ease-in-out infinite',
              }} />
              <span>Currently building → Project 5 of 8 — Local SLM with Ollama</span>
            </div>

            {/* Built with — icon row */}
            <div style={{ display: 'flex', gap: 24, marginBottom: 28, alignItems: 'flex-end' }}>
              {[
                { Icon: SiLangchain,  label: 'LangChain' },
                { Icon: SiOpenai,     label: 'OpenAI' },
                { Icon: BsDatabase,   label: 'ChromaDB' },
                { Icon: SiGradio,     label: 'Gradio' },
                { Icon: FaMicrophone, label: 'Whisper' },
              ].map(({ Icon, label }) => (
                <TechIcon key={label} Icon={Icon} label={label} />
              ))}
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Btn primary onClick={onViewProjects}>View projects</Btn>
              <Btn onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}>Get in touch</Btn>
            </div>

            {/* Stats strip */}
            <div style={{
              display: 'flex', gap: 0, marginTop: 52,
              borderTop: '0.5px solid var(--border)', paddingTop: 28,
            }}>
              {[
                { val: '4',     label: 'Projects shipped' },
                { val: '4.8/5', label: 'LLM judge score',  accent: true },
                { val: 'AI PM', label: 'Target role' },
              ].map(({ val, label, accent }, i) => (
                <div key={i} style={{
                  borderRight: i < 2 ? '0.5px solid var(--border)' : 'none',
                  paddingRight: 32, paddingLeft: i > 0 ? 32 : 0,
                  minWidth: 0,
                }}>
                  <p style={{
                    fontFamily: 'var(--serif)', fontSize: 28, fontWeight: 300,
                    fontStyle: accent ? 'italic' : 'normal',
                    color: accent ? 'var(--accent)' : 'var(--text)',
                    lineHeight: 1, marginBottom: 6,
                  }}>{val}</p>
                  <p style={{
                    fontFamily: 'var(--mono)', fontSize: 10,
                    color: 'var(--text-3)', letterSpacing: '0.12em', textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}>{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — terminal scales up to fill the screen */}
          <div
            ref={terminalWrapRef}
            style={{
              transformOrigin: '50% 50%',
              willChange: 'transform',
              position: 'relative', zIndex: 2,
            }}
          >
            <TerminalWithRef codeRef={terminalCodeRef} />
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="fade-4" style={{
          position: 'absolute', bottom: 36, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
          zIndex: 1,
        }}>
          <span style={{
            fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)',
            letterSpacing: '0.2em', textTransform: 'uppercase',
          }}>Scroll</span>
          <div style={{
            width: '0.5px', height: 44,
            background: 'linear-gradient(to bottom, var(--text-3), transparent)',
            animation: 'scrollDrop 2s ease-in-out infinite',
          }} />
        </div>
      </div>
    </section>
  );
}

function About() {
  const ref = useReveal();
  return (
    <section id="about" style={{ padding: '100px 0' }}>
      <div style={{ maxWidth: 'var(--max)', margin: '0 auto', padding: '0 60px' }}>
        <SectionLabel>About</SectionLabel>
        {/* 2×2 grid: [photo | bio] then [4 items | skill bars] — rows align naturally */}
        <div ref={ref} className="reveal" style={{
          display: 'grid',
          gridTemplateColumns: '300px 1fr',
          columnGap: 72,
          rowGap: 40,
        }}>

          {/* Row 1, Col 1 — photo */}
          <div>
            <img
              src="/images/headshot.jpg"
              alt="Ronak Panchal"
              style={{
                width: 230, height: 230, borderRadius: '50%',
                objectFit: 'cover', objectPosition: 'center 15%',
                transform: 'scale(1.3)', transformOrigin: 'center 30%',
                border: '0.5px solid var(--border-md)',
                display: 'block',
              }}
            />
          </div>

          {/* Row 1, Col 2 — bio text */}
          <div>
            <p style={{ fontSize: 17, fontWeight: 300, color: 'var(--text-2)', lineHeight: 1.9 }}>
              I'm an <strong style={{ color: 'var(--text)', fontWeight: 400 }}>AI/ML Engineer</strong> with a focus on building end-to-end intelligent systems — from semantic search pipelines to production RAG frameworks and evaluation tooling.<br /><br />
              My work sits at the intersection of <strong style={{ color: 'var(--text)', fontWeight: 400 }}>applied machine learning</strong> and <strong style={{ color: 'var(--text)', fontWeight: 400 }}>product thinking</strong>. I care deeply about not just how AI is built, but how it delivers real value to users.<br /><br />
              Currently building a portfolio of <strong style={{ color: 'var(--text)', fontWeight: 400 }}>8 AI engineering projects</strong> — spanning RAG, evaluation, local SLMs, fine-tuning, multi-agent systems, and voice AI — targeting an <strong style={{ color: 'var(--text)', fontWeight: 400 }}>AI Product Manager</strong> role.
            </p>
          </div>

          {/* Row 2, Col 1 — 4 items, aligned with RAG Engineering bar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 15, letterSpacing: '0.04em' }}>
              <div style={{ fontSize: 12, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>Location</div>
              <div style={{ color: 'var(--text-2)' }}>USA</div>
            </div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 15, letterSpacing: '0.04em' }}>
              <div style={{ fontSize: 12, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>Open to</div>
              <div style={{ color: 'var(--text-2)' }}>AI PM roles</div>
            </div>
            <a href="https://github.com/rpanchal126" target="_blank" rel="noreferrer"
              style={{ fontFamily: 'var(--mono)', fontSize: 15, color: 'var(--text-2)', textDecoration: 'none', letterSpacing: '0.04em' }}>
              GitHub ↗
            </a>
            <a href="https://www.linkedin.com/in/ronpanchal" target="_blank" rel="noreferrer"
              style={{ fontFamily: 'var(--mono)', fontSize: 15, color: 'var(--text-2)', textDecoration: 'none', letterSpacing: '0.04em' }}>
              LinkedIn ↗
            </a>
          </div>

          {/* Row 2, Col 2 — skill bars + download */}
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { label: 'RAG Engineering',    pct: 80 },
                { label: 'LLM Orchestration',  pct: 70 },
                { label: 'Prompt Engineering', pct: 70 },
                { label: 'AI Evaluation',      pct: 80 },
                { label: 'Python',             pct: 60 },
              ].map(s => <SkillBar key={s.label} label={s.label} pct={s.pct} />)}
            </div>
            <div style={{ marginTop: 32 }}>
              <a href="/Ronak_Panchal_Resume.pdf" download style={{ textDecoration: 'none' }}>
                <Btn small>Download Resume ↗</Btn>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Projects({ onOpenProject }) {
  const ref = useReveal();
  return (
    <section id="projects" style={{ paddingBottom: 100 }}>
      <div style={{ maxWidth: 'var(--max)', margin: '0 auto', padding: '0 60px' }}>
        <SectionLabel>Selected Work</SectionLabel>
        <div ref={ref} className="reveal" style={{ border: '0.5px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          {PROJECTS.map(p => (
            <ProjectCard key={p.num} project={p} onOpen={() => onOpenProject(p)} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Stack() {
  return (
    <section id="stack" style={{ padding: '100px 0' }}>
      <div style={{ maxWidth: 'var(--max)', margin: '0 auto', padding: '0 60px' }}>
        <SectionLabel>Stack</SectionLabel>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          border: '0.5px solid var(--border)', borderRadius: 10, overflow: 'hidden',
        }}>
          {STACK.map((s, i) => {
            const col = i % 3;
            const row = Math.floor(i / 3);
            const totalRows = Math.ceil(STACK.length / 3);
            return (
              <StackCell key={s.cat} cat={s.cat} items={s.items}
                borderRight={col < 2} borderBottom={row < totalRows - 1} />
            );
          })}
        </div>
      </div>
    </section>
  );
}

function StackCell({ cat, items, borderRight, borderBottom }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '22px 26px',
        borderRight: borderRight ? '0.5px solid var(--border)' : 'none',
        borderBottom: borderBottom ? '0.5px solid var(--border)' : 'none',
        background: hovered ? 'var(--bg2)' : 'transparent',
        transition: 'background 0.2s',
      }}
    >
      <p style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 10 }}>{cat}</p>
      <p style={{ fontSize: 13, fontWeight: 300, color: 'var(--text-2)', lineHeight: 2.1 }}>
        {items.map((item, i) => <span key={i}>{item}{i < items.length - 1 && <br />}</span>)}
      </p>
    </div>
  );
}

function Contact() {
  const ref = useReveal();
  return (
    <section id="contact" style={{ padding: '100px 0', borderTop: '0.5px solid var(--border)' }}>
      <div style={{ maxWidth: 'var(--max)', margin: '0 auto', padding: '0 60px' }}>
        <div ref={ref} className="reveal" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center' }}>
          <div>
            <h2 style={{
              fontFamily: 'var(--serif)', fontSize: 'clamp(30px, 4vw, 50px)',
              fontWeight: 300, fontStyle: 'italic', lineHeight: 1.2,
              letterSpacing: '-0.02em', color: 'var(--text)', marginBottom: 12,
            }}>
              Let's build something worth shipping.
            </h2>
            <p style={{ fontSize: 14, fontWeight: 300, color: 'var(--text-2)', lineHeight: 1.7 }}>
              Open to AI PM roles, collaborations,<br />and interesting conversations.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <ContactLink href="mailto:rnpanchal126@gmail.com" label="Email me" sub="rnpanchal126@gmail.com ↗" />
            <ContactLink href="https://github.com/rpanchal126" label="GitHub" sub="github.com/rpanchal126 ↗" external />
            <ContactLink href="https://www.linkedin.com/in/ronpanchal" label="LinkedIn" sub="linkedin.com/in/ronak-panchal ↗" external />
            <ContactLink href="/Ronak_Panchal_Resume.pdf" label="Resume" sub="Download PDF ↗" download />
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer style={{
      borderTop: '0.5px solid var(--border)', padding: '26px 60px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', letterSpacing: '0.05em' }}>Ronak Panchal</span>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-3)', letterSpacing: '0.05em' }}>AI/ML · PM</span>
    </footer>
  );
}

// ── APP ─────────────────────────────────────────────────
export default function App() {
  const [currentProject, setCurrentProject] = useState(null);

  const openProject = (project) => {
    setCurrentProject(project);
    window.scrollTo({ top: 0 });
  };

  const goHome = (scrollToId) => {
    setCurrentProject(null);
    if (scrollToId) {
      setTimeout(() => {
        document.getElementById(scrollToId)?.scrollIntoView({ behavior: 'smooth' });
      }, 80);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <style>{css}</style>
      <Nav goHome={goHome} isProjectPage={!!currentProject} />
      {currentProject ? (
        <>
          <ProjectPage project={currentProject} onBack={() => goHome('projects')} />
          <Footer />
        </>
      ) : (
        <>
          <Hero onViewProjects={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })} />
          <About />
          <Projects onOpenProject={openProject} />
          <Stack />
          <Contact />
          <Footer />
        </>
      )}
    </>
  );
}
